import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Clock, CheckCircle,
  Video, FileText, ChevronRight, GitCompare,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useInterviewSimSessions } from '../hooks/cabin/useInterviewSimSessions';
import SimSessionHistoryItem from '../components/cabin/interview/SimSessionHistoryItem';

/**
 * CabinInterviewHistory — /programs/kabin-memurlugu/interview/history
 *
 * Öğrenci oturum listesini gösterir. "İncele" aksiyonu artık modal açmaz;
 * /programs/kabin-memurlugu/interview/session/:sessionId route'una navigate eder (Ekran 5).
 */
export default function CabinInterviewHistory() {
  const navigate = useNavigate();
  const { lang, t } = useLanguage();

  const { sessions, loading, error, refetch } = useInterviewSimSessions();

  const scored     = sessions.filter(s => s.status === 'scored');
  const pending    = sessions.filter(s => s.status === 'pending_review');
  const scoredV3   = sessions.filter(s => s.status === 'scored' && s.schema_version === 3);

  const handleInspect = (sessionId) => {
    navigate(`/programs/kabin-memurlugu/interview/session/${sessionId}`);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-app)] flex flex-col">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b-2 border-gray-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/programs/kabin-memurlugu/interview')}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label={t('cabin.interview.text.back')}
        >
          <ArrowLeft size={18} aria-hidden="true" />
        </button>
        <h1 className="font-display text-base font-black text-[var(--text-main)] flex-1">
          {t('cabin.interview.history.title')}
        </h1>
      </div>

      <div className="flex-1 p-4 md:p-6 max-w-2xl mx-auto w-full flex flex-col gap-5">
        {loading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 rounded-2xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12 text-gray-400">
            <p className="font-medium mb-3">{error}</p>
            <button
              type="button"
              onClick={refetch}
              className="px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] border-2 border-[var(--brand-primary-shadow)] text-white text-sm font-bold hover:opacity-90 transition-opacity"
            >
              {t('common.retry')}
            </button>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Video size={36} className="mx-auto mb-3 opacity-40" aria-hidden="true" />
            <p className="font-medium">{t('cabin.interview.history.no_sessions')}</p>
            <button
              type="button"
              onClick={() => navigate('/programs/kabin-memurlugu/interview')}
              className="mt-4 px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] border-2 border-[var(--brand-primary-shadow)] text-white text-sm font-bold hover:opacity-90 transition-opacity"
            >
              {t('cabin.interview.history.start_first')}
            </button>
          </div>
        ) : (
          <>
            {/* F5: Readiness Report entry banner */}
            {scored.length > 0 && (
              <motion.button
                type="button"
                onClick={() => navigate('/programs/kabin-memurlugu/interview/readiness-report')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-[color-mix(in_srgb,var(--brand-primary)_8%,white)] to-indigo-50 border-2 border-[color-mix(in_srgb,var(--brand-primary)_30%,white)] shadow-[2px_2px_0px_0px_rgba(0,0,0,0.06)] hover:from-[color-mix(in_srgb,var(--brand-primary)_12%,white)] hover:to-indigo-100 transition-colors text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
                aria-label={t('readinessReport.entry.title')}
              >
                <div className="w-10 h-10 rounded-xl bg-[color-mix(in_srgb,var(--brand-primary)_12%,white)] border-2 border-[color-mix(in_srgb,var(--brand-primary)_25%,white)] flex items-center justify-center shrink-0">
                  <FileText size={18} className="text-[var(--brand-primary)]" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[var(--text-main)]">{t('readinessReport.entry.title')}</p>
                  <p className="text-xs text-[var(--brand-primary)] font-semibold mt-0.5">{t('readinessReport.entry.available')}</p>
                </div>
                <ChevronRight size={18} className="text-[var(--brand-primary)] shrink-0" aria-hidden="true" />
              </motion.button>
            )}
            {/* Disabled state when pending but no scored sessions */}
            {scored.length === 0 && pending.length > 0 && (
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 border-2 border-gray-200 opacity-60 cursor-default">
                <div className="w-10 h-10 rounded-xl bg-gray-100 border-2 border-gray-200 flex items-center justify-center shrink-0">
                  <FileText size={18} className="text-gray-400" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm text-gray-500">{t('readinessReport.entry.title')}</p>
                  <p className="text-xs text-gray-400 font-semibold mt-0.5">{t('readinessReport.entry.pending')}</p>
                </div>
              </div>
            )}

            {/* F3: Compare banner — active if scoredV3 >= 2 */}
            {scoredV3.length >= 2 ? (
              <motion.button
                type="button"
                onClick={() => navigate('/programs/kabin-memurlugu/interview/readiness-report/compare')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-indigo-50 to-[color-mix(in_srgb,var(--brand-primary)_6%,white)] border-2 border-indigo-200 shadow-[2px_2px_0px_0px_rgba(0,0,0,0.06)] hover:from-indigo-100 hover:to-[color-mix(in_srgb,var(--brand-primary)_10%,white)] transition-colors text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
                aria-label={t('readinessReport.compare.entryBanner.title')}
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-100 border-2 border-indigo-200 flex items-center justify-center shrink-0">
                  <GitCompare size={18} className="text-indigo-600" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[var(--text-main)]">{t('readinessReport.compare.entryBanner.title')}</p>
                  <p className="text-xs text-indigo-600 font-semibold mt-0.5">{t('readinessReport.compare.entryBanner.sub')}</p>
                </div>
                <ChevronRight size={18} className="text-indigo-400 shrink-0" aria-hidden="true" />
              </motion.button>
            ) : scoredV3.length === 1 ? (
              <div
                className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gray-50 border-2 border-gray-200 opacity-60 cursor-default"
                title={t('readinessReport.compare.entryBanner.disabled')}
                aria-disabled="true"
                aria-label={`${t('readinessReport.compare.entryBanner.title')} — ${t('readinessReport.compare.entryBanner.disabled')}`}
              >
                <div className="w-10 h-10 rounded-xl bg-gray-100 border-2 border-gray-200 flex items-center justify-center shrink-0">
                  <GitCompare size={18} className="text-gray-400" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-gray-500">{t('readinessReport.compare.entryBanner.title')}</p>
                  <p className="text-xs text-gray-400 font-semibold mt-0.5">{t('readinessReport.compare.entryBanner.disabled')}</p>
                </div>
              </div>
            ) : null}

            {/* Pending section */}
            {pending.length > 0 && (
              <div>
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <Clock size={13} aria-hidden="true" />
                  {t('interview.sim.status_pending')} ({pending.length})
                </h2>
                <div className="flex flex-col gap-2">
                  {pending.map((s) => (
                    <SimSessionHistoryItem
                      key={s.session_id}
                      session={s}
                      onClick={() => handleInspect(s.session_id)}
                      t={t}
                      lang={lang}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Scored section */}
            {scored.length > 0 && (
              <div>
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <CheckCircle size={13} aria-hidden="true" />
                  {t('interview.sim.status_scored')} ({scored.length})
                </h2>
                <div className="flex flex-col gap-2">
                  {scored.map((s) => (
                    <SimSessionHistoryItem
                      key={s.session_id}
                      session={s}
                      onClick={() => handleInspect(s.session_id)}
                      t={t}
                      lang={lang}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
